import { isObject } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Input } from '@chakra-ui/react';
import styled from 'styled-components';

import { getSellingPlansIntervalOptions } from '../../components/Subscriptions/utils';
import CallToAction from '../components/Card/CallToAction';
import useForm from '../utils/useForm';
import { useLoadingToast } from '../utils/useLoadingToast';
import { updateFrequency } from './api';
import { useGlobalContext } from './state/GlobalContext';
import Card from '../components/Card';
import { formatFrequency } from './utils';

const Container = styled.div.attrs({
  className: 'awt-frequency-container',
})`
  padding-top: 0;
`;
interface IProps {
  subscription;
  onUpdate: (res: SubscriptionFrequency, msg: string) => void;
  onError: (msg: string) => void;
  isPrepaid: boolean;
  isPrepaidV2?: boolean;
  isAutoRenew?: boolean;
  isBillingOrUpdating?: boolean;
  availableSellingPlans?: SellingPlan[];
}

const DEFAULT_INTERVALS = {
  biweekly: 'WEEK#2',
  monthly: 'MONTH#1',
  bimonthly: 'MONTH#2',
};

export default ({
  subscription,
  onUpdate,
  onError,
  isPrepaid,
  isPrepaidV2,
  isBillingOrUpdating,
  availableSellingPlans = [],
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { customerId, subscriptionId, authToken, customerPortalSettings } =
    useGlobalContext();

  const [showEditFrequency, setShowEditFrequency] = useState(false);
  const [loading, setLoading] = useState(false);

  const { addLoadingToast, removeLoadingToast } = useLoadingToast();

  const saveFrequency = async (values) => {
    try {
      setLoading(true);
      addLoadingToast();
      const res = await updateFrequency(customerId, subscriptionId, authToken, {
        intervalCount: parseInt(values.intervalCount),
        interval: values.interval,
        isCustom: values.isCustom,
      });
      onUpdate(
        res,
        t('subscription.frequency_update_success', {
          frequency: formatFrequency(
            t,
            res.DeliveryIntervalDeliveryCount,
            res.FormattedFrequency,
          ),
        }),
      );
      setShowEditFrequency(false);
    } catch (err) {
      console.error(err);
      onError(t('subscription.frequency_update_error'));
    } finally {
      setLoading(false);
      removeLoadingToast();
    }
  };

  const isActive = subscription?.SubscriptionStatus === 'ACTIVE';
  const merchantSettingHideEditFrequency =
    customerPortalSettings?.hideEditFrequency;
  const merchantSettingHideCustomFrequency =
    customerPortalSettings?.hideCustomFrequency;
  const isBatching =
    isObject(subscription?.BatchingConfiguration) &&
    subscription?.BatchingConfiguration?.anchor !== '';

  return (
    <Card
      ariaLabel={
        !showEditFrequency
          ? t('aria_labels.edit_frequency')
          : t('aria_labels.cancel_edit_frequency')
      }
      headerText={t('tables.frequency')}
      btnText={!showEditFrequency ? t('titles.edit') : t('titles.cancel')}
      disableButton={isPrepaid}
      onClick={() => setShowEditFrequency(!showEditFrequency)}
      className="bundle-card--edit-frequency"
      showButton={
        isActive &&
        !isPrepaidV2 &&
        !isBillingOrUpdating &&
        !merchantSettingHideEditFrequency
      }
    >
      <Container>
        {!showEditFrequency
          ? formatFrequency(
              t,
              subscription.DeliveryIntervalDeliveryCount,
              subscription.FormattedFrequency,
            )
          : null}

        {showEditFrequency && (
          <FrequencySelector
            saveFrequency={saveFrequency}
            isLoading={loading}
            isBatching={isBatching}
            currentFrequency={subscription?.BillingIntervalBillingCount}
            availableSellingPlans={availableSellingPlans}
            merchantSettingHideCustomFrequency={
              merchantSettingHideCustomFrequency
            }
          />
        )}
      </Container>
    </Card>
  );
};

const FrequencySelector = ({
  saveFrequency,
  isLoading,
  isBatching,
  currentFrequency,
  availableSellingPlans,
  merchantSettingHideCustomFrequency,
}: {
  saveFrequency: (values: {
    interval: string;
    intervalCount: string;
    isCustom: boolean;
  }) => void;
  isLoading: boolean;
  isBatching: boolean;
  currentFrequency: string;
  availableSellingPlans: SellingPlan[];
  merchantSettingHideCustomFrequency?: boolean;
}) => {
  const isCurrentFrequencyCustom = currentFrequency === 'CUSTOM';
  const initialFrequency =
    isCurrentFrequencyCustom && merchantSettingHideCustomFrequency
      ? DEFAULT_INTERVALS.biweekly
      : currentFrequency;

  const { t } = useTranslation();
  const [selected, setSelected] = useState(
    initialFrequency || DEFAULT_INTERVALS.biweekly,
  );
  const [interval, intervalCount] = currentFrequency.split('#');
  const { values, handleChange: handleOnCustomChange } = useForm<{
    intervalCount: string;
    interval: string;
  }>({
    initialValues: {
      intervalCount,
      interval,
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    setSelected(selected);
  };

  const handleFrequencySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const [interval, intervalCount] = selected.split('#');

    saveFrequency({
      intervalCount:
        selected === 'CUSTOM' ? values.intervalCount : intervalCount,
      interval: selected === 'CUSTOM' ? values.interval : interval,
      isCustom: selected === 'CUSTOM',
    });
  };

  const CUSTOM_OPTION = {
    label: t('subscription.custom'),
    value: 'CUSTOM',
  };
  const DEFAULT_INTERVAL_OPTIONS = [
    {
      label: t('subscription.frequency_2weeks'),
      value: DEFAULT_INTERVALS.biweekly,
    },
    {
      label: t('subscription.frequency_1month'),
      value: DEFAULT_INTERVALS.monthly,
    },
    {
      label: t('subscription.frequency_2months'),
      value: DEFAULT_INTERVALS.bimonthly,
    },
  ];
  const availableOptions = getSellingPlansIntervalOptions(
    availableSellingPlans,
  );
  const options = availableOptions.length
    ? availableOptions
    : DEFAULT_INTERVAL_OPTIONS;

  if (!isBatching && !merchantSettingHideCustomFrequency) {
    options.push(CUSTOM_OPTION);
  }

  useEffect(() => {
    const usedOption = options.find((option) => option.value === selected);
    const defaultOption = merchantSettingHideCustomFrequency
      ? options[0].value
      : 'CUSTOM';

    setSelected(usedOption ? usedOption.value : defaultOption);
  }, []);

  return (
    <form onSubmit={handleFrequencySubmit}>
      <div
        className="bundleapp-flex"
        style={{ marginBottom: 10, marginTop: 10 }}
      >
        <Select
          aria-label={t('tables.frequency')}
          id="interval"
          name="interval"
          value={selected}
          onChange={handleChange}
          required
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>
      {selected === 'CUSTOM' && !merchantSettingHideCustomFrequency && (
        <FrequencyCustomSelector
          handleOnChange={handleOnCustomChange}
          interval={values.interval}
          intervalCount={values.intervalCount}
        />
      )}
      <CallToAction
        type="submit"
        onClick={handleFrequencySubmit}
        isLoading={isLoading}
        isDisabled={isLoading}
      >
        {t('titles.save')}
      </CallToAction>
    </form>
  );
};

const FrequencyCustomSelector = ({
  handleOnChange,
  interval,
  intervalCount,
}: {
  handleOnChange: (e: React.ChangeEvent<any>) => void;
  interval: string;
  intervalCount: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className="bundleapp-flex" style={{ marginBottom: 10 }}>
      <Input
        type="text"
        pattern="[0-9]{1,2}"
        name="intervalCount"
        inputMode="numeric"
        placeholder={t('subscription.amount')}
        value={intervalCount}
        onChange={handleOnChange}
        required
        width="44px"
        marginRight="10px"
      />
      <Select
        id="interval"
        name="interval"
        value={interval}
        onChange={handleOnChange}
        required
      >
        <option value="DAY">{t('subscription.frequency_days')}</option>
        <option value="WEEK">{t('subscription.frequency_weeks')}</option>
        <option value="MONTH">{t('subscription.frequency_months')}</option>
      </Select>
    </div>
  );
};
